/* =========================================================
   Free Shipping Bar — page + drawer (robust updater)
   ========================================================= */

(() => {
  const BAR_SELECTOR = '.gea-free-ship';
  const DEFAULT_THRESHOLD = 19900; // $199.00 in cents
  const MONEY_FMT = (window.Shopify && Shopify.money_format) || "${{amount}}";

  /* ----------------- Money helpers ----------------- */
  const fmt = (cents, format) => {
    try {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, format);
      }
    } catch (e) {}
    // 16 Oct 2026: Shopify.formatMoney isn't on the page, so build the amount from the shop's format (adds the $).
    const value = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return String(format || MONEY_FMT).replace(/\{\{\s*amount[a-z_]*\s*\}\}/, value);
  };

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return await res.json();
  }

  /* ----------------- Drawer helpers ----------------- */
  const DRAWER_CANDIDATES = [
    '#CartDrawer', '.cart-drawer', 'cart-drawer',
    '[data-cart-drawer]', '.ajaxcart', '.mini-cart',
    '.cart-notification', 'cart-notification', '#CartNotification',
    '[role="dialog"][aria-label*="Cart"]', '[role="dialog"][id*="Cart"]'
  ];

  function findDrawer() {
    for (const sel of DRAWER_CANDIDATES) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function findDrawerFooter(drawer) {
    return (
      drawer.querySelector('.drawer__footer') ||
      drawer.querySelector('.cart-drawer__footer') ||
      drawer.querySelector('.ajaxcart__footer') ||
      drawer.querySelector('.mini-cart__footer') ||
      drawer.querySelector('.cart__ctas') ||
      drawer.querySelector('[data-drawer-footer]') ||
      drawer.querySelector('[data-shopify="cart-subtotal"]') ||
      drawer
    );
  }

  function drawerBarHTML() {
    const mf = String(MONEY_FMT).replace(/"/g, '&quot;');
    return `
<div class="gea-free-ship gea-free-ship--drawer"
     role="region"
     aria-label="Free shipping progress (drawer)"
     data-threshold="${DEFAULT_THRESHOLD}"
     data-money-format="${mf}">
  <div class="gea-free-ship__bar" aria-hidden="false">
    <div class="gea-free-ship__fill" style="width:0%"></div>
    <div class="gea-ms" aria-hidden="true">
      <div class="gea-ms__item" data-key="m2" style="left:96%">
        <span class="gea-ms__circle" aria-hidden="true">
          <svg class="icon--lock" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 7V5a4 4 0 118 0v2h1a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1V8a1 1 0 011-1h1zm2 0h4V5a2 2 0 10-4 0v2z"/>
          </svg>
          <svg class="icon--tick" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M6.5 11.5l-3-3 1.4-1.4 1.6 1.6 4-4 1.4 1.4-5.4 5.4z"/>
          </svg>
          <span class="gea-ms__title">$199</span>
        </span>
        <span class="gea-ms__label">Free Standard Shipping</span>
      </div>
    </div>
  </div>
  <p class="gea-free-ship__msg">
    <span class="gea-free-ship__text" data-state="progress">
      Spend <span class="gea-amount"></span> more to unlock <strong>FREE shipping</strong>.
    </span>
    <span class="gea-free-ship__text gea-free-ship__text--success" data-state="success" hidden>
      Yay! You’ve qualified for <strong>FREE shipping</strong>! 🎉
    </span>
  </p>
</div>`;
  }

  function ensureDrawerBar() {
    const drawer = findDrawer();
    if (!drawer) return;

    if (!drawer.querySelector('.gea-free-ship--drawer')) {
      const host = findDrawerFooter(drawer);
      const wrap = document.createElement('div');
      wrap.innerHTML = drawerBarHTML();
      const bar = wrap.firstElementChild;
      // insert just above subtotal/buttons
      host.parentNode.insertBefore(bar, host);
    }
  }

  /* ----------------- Country (16 Oct 2026) ----------------- */
  // Free standard shipping over $199 only exists for these delivery zones (checked against the
  // General delivery profile 16 Oct 2026). Everyone else gets no free-shipping bar.
  const FREE_SHIP_THRESHOLDS = { AU: 19900, NZ: 19900 };
  let COUNTRY = null;

  function setCountry(code) {
    COUNTRY = code || null;
    const noFree = !!(COUNTRY && !FREE_SHIP_THRESHOLDS[COUNTRY]);
    document.documentElement.classList.toggle('mea-no-free-ship', noFree);
  }

  async function detectCountry() {
    try {
      const forced = new URLSearchParams(location.search).get('mea_country'); // testing aid
      if (forced) sessionStorage.setItem('mea_visitor_country', forced.toUpperCase());
      const cached = sessionStorage.getItem('mea_visitor_country');
      if (cached) return setCountry(cached);
    } catch (e) {}
    try {
      const res = await fetch('/browsing_context_suggestions.json?country[enabled]=true&language[enabled]=false', { credentials: 'same-origin' });
      const data = await res.json();
      const code = data && data.detected_values && data.detected_values.country && data.detected_values.country.handle;
      if (code) {
        try { sessionStorage.setItem('mea_visitor_country', code); } catch (e) {}
        setCountry(code);
        triggerUpdate(0);
      }
    } catch (e) {}
  }

  /* ----------------- Qualify popper (16 Oct 2026) ----------------- */
  // Fires once when the cart crosses the free-shipping line in this visit, not on every page.
  function popper() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const canvas = document.createElement('canvas');
    canvas.className = 'gea-popper';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = window.innerWidth, H = window.innerHeight;
    canvas.width = W * dpr; canvas.height = H * dpr; ctx.scale(dpr, dpr);
    const colours = ['#1a237e', '#ffd600', '#3d5afe', '#ffffff', '#ffb300', '#5c6bc0'];
    const bar = document.querySelector('#gea-free-ship');
    const top = bar ? Math.max(10, bar.getBoundingClientRect().bottom - 8) : 40;
    const parts = [];
    [[W * 0.12, 1], [W * 0.88, -1]].forEach(([x, dir]) => {
      for (let i = 0; i < 70; i++) {
        const streamer = i % 4 === 0;
        const speed = 7 + Math.random() * 9;
        const angle = (Math.PI / 2.6) * Math.random() + Math.PI / 10; // up and inwards
        parts.push({
          x, y: top, streamer,
          vx: dir * Math.cos(angle) * speed * (0.6 + Math.random() * 0.7),
          vy: -Math.sin(angle) * speed,
          w: streamer ? 3 : 6 + Math.random() * 4, h: streamer ? 26 + Math.random() * 22 : 4 + Math.random() * 4,
          rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.3,
          wob: Math.random() * 10, c: colours[(Math.random() * colours.length) | 0]
        });
      }
    });
    const start = performance.now(), life = 2600;
    function frame(t) {
      const el = t - start;
      ctx.clearRect(0, 0, W, H);
      const fade = el > life - 600 ? Math.max(0, (life - el) / 600) : 1;
      parts.forEach(p => {
        p.vy += 0.32; p.vx *= 0.985; p.vy *= p.streamer ? 0.97 : 0.985;
        p.x += p.vx + Math.sin((el / 160) + p.wob) * (p.streamer ? 1.4 : 0.6);
        p.y += p.vy; p.rot += p.vr;
        ctx.save(); ctx.globalAlpha = fade; ctx.translate(p.x, p.y); ctx.fillStyle = p.c; ctx.strokeStyle = p.c;
        if (p.streamer) {
          ctx.rotate(p.rot * 0.3); ctx.lineWidth = p.w; ctx.lineCap = 'round'; ctx.beginPath();
          for (let k = 0; k <= 6; k++) { const yy = (k / 6) * p.h; const xx = Math.sin(k * 1.3 + el / 120 + p.wob) * 4; k ? ctx.lineTo(xx, yy) : ctx.moveTo(xx, yy); }
          ctx.stroke();
        } else {
          ctx.rotate(p.rot); ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      });
      if (el < life) requestAnimationFrame(frame); else canvas.remove();
    }
    requestAnimationFrame(frame);
  }

  function checkQualified(total) {
    const threshold = COUNTRY ? FREE_SHIP_THRESHOLDS[COUNTRY] : FREE_SHIP_THRESHOLDS.AU;
    if (!threshold) return;
    const now = total >= threshold ? '1' : '0';
    let prev = null;
    try { prev = sessionStorage.getItem('mea_fs_qualified'); sessionStorage.setItem('mea_fs_qualified', now); } catch (e) {}
    if (prev === '0' && now === '1') popper();
  }

  /* ----------------- Core updater ----------------- */
  function updateBar(el, cart) {
    const threshold = parseInt(el.dataset.threshold, 10) || DEFAULT_THRESHOLD;
    const moneyFmt  = el.dataset.moneyFormat || MONEY_FMT;
    const total     = cart.total_price || 0;
    const remaining = Math.max(0, threshold - total);
    const pct       = Math.min(100, Math.max(0, (total / threshold) * 100));

    const fill = el.querySelector('.gea-free-ship__fill');
    if (fill) fill.style.width = pct + '%';

    const prog = el.querySelector('.gea-free-ship__text[data-state="progress"]');
    const succ = el.querySelector('.gea-free-ship__text[data-state="success"]');
    if (prog) prog.hidden = remaining <= 0;
    if (succ) succ.hidden = !(remaining <= 0);

    const amt = el.querySelector('.gea-amount');
    if (amt) amt.textContent = fmt(remaining, moneyFmt);

    const m2 = el.querySelector('.gea-ms__item[data-key="m2"]');
    if (m2) m2.classList.toggle('unlocked', remaining <= 0);

    el.classList.toggle('gea-free-ship--qualified', remaining <= 0);
  }

  let _timer = null;
  function triggerUpdate(delay = 120) {
    clearTimeout(_timer);
    _timer = setTimeout(updateAll, delay);
  }

  async function updateAll() {
    // Make sure the drawer has a bar we can update
    ensureDrawerBar();

    const bars = document.querySelectorAll(BAR_SELECTOR);
    if (!bars.length) return;

    let cart;
    try {
      cart = await fetchCart();
    } catch (e) {
      return;
    }

    bars.forEach(el => {
      try { updateBar(el, cart); } catch {}
    });

    // 16 Oct 2026: let the product-page shipping estimate know the cart total, and pop on qualifying.
    try {
      window.__MEA_CART = { total: cart.total_price || 0, country: COUNTRY };
      document.dispatchEvent(new CustomEvent('mea:cart', { detail: window.__MEA_CART }));
    } catch (e) {}
    checkQualified(cart.total_price || 0);
  }

  /* ----------------- Event wiring ----------------- */

  // 1) Common theme events
  [
    'cart:updated','cart:change','cart:request:complete',
    'ajaxProduct:added','product:added','variant:change',
    'shopify:section:load','cart:refresh'
  ].forEach(evt => document.addEventListener(evt, () => triggerUpdate(60)));

  // 2) Intercept fetch() calls to cart endpoints (apps/themes)
  (function patchFetch() {
    if (!window.fetch || window.__FSB_FETCH_PATCHED__) return;
    const orig = window.fetch;
    window.fetch = function(input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const hitsCart = /\/cart(\/(add|change|update|clear)\.js|\.(add|change|update|clear))(\?|$)/.test(url);
      return orig(input, init).then(res => {
        if (hitsCart) Promise.resolve().then(() => triggerUpdate(80));
        return res;
      });
    };
    window.__FSB_FETCH_PATCHED__ = true;
  })();

  // 3) Intercept legacy XHR cart calls too
  (function patchXHR() {
    if (!window.XMLHttpRequest || window.__FSB_XHR_PATCHED__) return;
    const open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url) {
      this.__fsb_cart_hit = /\/cart(\/(add|change|update|clear)\.js|\.(add|change|update|clear))(\?|$)/.test(url || '');
      return open.apply(this, arguments);
    };
    const send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function() {
      this.addEventListener('load', () => { if (this.__fsb_cart_hit) triggerUpdate(80); }, { once: true });
      return send.apply(this, arguments);
    };
    window.__FSB_XHR_PATCHED__ = true;
  })();

  // 4) Observe DOM so when a cart drawer opens/rerenders we inject & update.
  //    16 Oct 2026: only react when a drawer is missing its bar. Reacting to every page change
  //    (e.g. the dispatch countdown ticking each second) re-fetched /cart.js ~3 times a second, forever.
  //    Real cart changes are still caught by the events and fetch/XHR hooks above.
  new MutationObserver(() => {
    const drawer = findDrawer();
    if (drawer && !drawer.querySelector('.gea-free-ship--drawer')) triggerUpdate(150);
  }).observe(document.documentElement, { childList: true, subtree: true });

  // 5) First run
  detectCountry();
  triggerUpdate(0);
})();
